import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del, openDmsFile } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { relative } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Mail, Paperclip, Sparkles, LogIn, Unplug, ChevronDown, ChevronUp, RefreshCw, CheckCircle2, Circle, Trash2 } from "lucide-react";
import { toast } from "sonner";

function MailRow({ m, atts, isOpen, onToggleExpand, onToggleAddressed, onDelete, pending }) {
  return (
    <div data-testid={`mail-row-${m.id}`} className="group">
      <div onClick={onToggleExpand}
        className={cn("flex w-full cursor-pointer items-start justify-between gap-3 px-4 py-3 text-left text-sm hover:bg-muted/50", !m.read && "bg-primary/[0.03]")}>
        <button type="button" title={m.addressed ? "Mark as needs attention" : "Mark as addressed"} data-testid={`addressed-toggle-${m.id}`}
          onClick={(e) => { e.stopPropagation(); onToggleAddressed(); }} disabled={pending}
          className="mt-0.5 shrink-0 text-muted-foreground hover:text-primary disabled:opacity-50">
          {m.addressed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4" />}
        </button>
        {!m.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" data-testid={`unread-dot-${m.id}`} />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={cn("font-medium", !m.read && "font-semibold")}>{m.from_name || m.from_email}</span>
            {atts.length > 0 && <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />}
          </div>
          <div className={cn("truncate text-sm", !m.read && "font-medium")}>{m.subject || "(no subject)"}</div>
          <div className="truncate text-xs text-muted-foreground">{m.snippet}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          {relative(m.received_at)}
          <div onClick={(e) => e.stopPropagation()}>
            <ConfirmDelete testid={`del-mail-${m.id}`} title="Remove this message from Iris?"
              description="This only removes it from Iris — it stays in your actual Gmail inbox."
              trigger={<button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
              onConfirm={onDelete} />
          </div>
          {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </div>
      </div>
      {isOpen && (
        <div className="border-t border-border/60 bg-muted/20 px-4 py-3 text-sm">
          <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" />
            {m.ai_summary || "AI summary not available yet."}
          </p>
          <p className="whitespace-pre-wrap text-sm text-foreground">{m.body_text?.slice(0, 2000) || m.snippet}</p>
          {atts.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {atts.map((a) => (
                <button key={a.id} onClick={() => openDmsFile(a.id)}
                  className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs hover:border-primary/40">
                  <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />{a.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Mailbox() {
  const { me } = useApp();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const { data: status, isLoading: statusLoading } = useQuery({ queryKey: ["mailbox-status"], queryFn: () => get("/mailbox/status") });
  const { data: messages = [], isLoading: messagesLoading } = useQuery({
    queryKey: ["mailbox"], queryFn: () => get("/mailbox"), enabled: !!status?.connected,
  });
  const { data: attachments = [] } = useQuery({
    queryKey: ["dms-files", "email", me?.id], queryFn: () => get(`/dms/files?entity_type=email&entity_id=${me.id}`),
    enabled: !!status?.connected && !!me,
  });
  const attachmentsByMessage = useMemo(() => {
    const map = {};
    for (const a of attachments) (map[a.source_message_id] ||= []).push(a);
    return map;
  }, [attachments]);

  useEffect(() => {
    if (params.get("connected")) { toast.success("Google account connected"); setParams({}, { replace: true }); }
    else if (params.get("google_error")) { toast.error("Could not connect Google account — please try again"); setParams({}, { replace: true }); }
  }, [params, setParams]);

  const connect = useMutation({
    mutationFn: () => get(`/hrms/employees/${me.id}/google/connect`),
    onSuccess: (res) => { window.location.href = res.auth_url; },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not start Google connection"),
  });
  const disconnect = useMutation({
    mutationFn: () => del(`/hrms/employees/${me.id}/google`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mailbox-status"] }); qc.invalidateQueries({ queryKey: ["me"] }); toast.success("Google account disconnected"); },
  });
  const sync = useMutation({
    mutationFn: () => post("/mailbox/sync"),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["mailbox"] });
      qc.invalidateQueries({ queryKey: ["dms-files", "email", me?.id] });
      qc.invalidateQueries({ queryKey: ["mailbox-status"] });
      qc.invalidateQueries({ queryKey: ["mailbox-unread-count"] });
      if (!res.ok) toast.error(res.error === "not_connected" ? "Not connected" : `Sync failed: ${res.error}`);
      else if (res.ingested > 0) toast.success(`Synced — ${res.ingested} new message${res.ingested === 1 ? "" : "s"}`);
      else toast.success("Synced — no new mail");
    },
  });
  const markRead = useMutation({
    mutationFn: (mid) => patch(`/mailbox/${mid}`, { read: true }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mailbox"] }); qc.invalidateQueries({ queryKey: ["mailbox-unread-count"] }); },
  });
  const toggleAddressed = useMutation({
    mutationFn: ({ mid, addressed }) => patch(`/mailbox/${mid}`, { addressed }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mailbox"] }),
  });
  const deleteMessage = useMutation({
    mutationFn: (mid) => del(`/mailbox/${mid}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mailbox"] });
      qc.invalidateQueries({ queryKey: ["dms-files", "email", me?.id] });
      qc.invalidateQueries({ queryKey: ["mailbox-unread-count"] });
      toast.success("Removed from Iris");
    },
  });

  const filtered = useMemo(() => {
    return messages.filter((m) =>
      !search || `${m.from_name} ${m.from_email} ${m.subject}`.toLowerCase().includes(search.toLowerCase())
    );
  }, [messages, search]);
  const needsAttention = useMemo(() => filtered.filter((m) => !m.addressed), [filtered]);
  const addressedList = useMemo(() => filtered.filter((m) => m.addressed), [filtered]);

  const isCompanyEmail = (me?.email || "").toLowerCase().endsWith("@inewtech.in");

  if (statusLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Mailbox" subtitle="Mail synced from your connected Google account.">
        {status?.connected && (
          <Button variant="outline" size="sm" data-testid="sync-now-btn" onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", sync.isPending && "animate-spin")} />Sync Now
          </Button>
        )}
      </PageHeader>

      {!status?.connected ? (
        <Section>
          <div className="flex flex-col items-center gap-4 p-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-muted-foreground"><Mail className="h-6 w-6" /></div>
            <div>
              <h3 className="font-display text-base font-semibold">Connect your Google Account</h3>
              <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground">
                {isCompanyEmail
                  ? "Your full inbox will be synced since you're on the company's Google Workspace domain."
                  : "Only messages from senders on the company's approved contact list will be synced — the rest of your personal inbox is never touched."}
              </p>
            </div>
            <Button data-testid="connect-google-btn" onClick={() => connect.mutate()} disabled={connect.isPending}>
              <LogIn className="mr-1.5 h-4 w-4" />Connect Google Account
            </Button>
          </div>
        </Section>
      ) : (
        <>
          <Section className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <div className="text-sm font-medium">{status.google_account_email}</div>
                <div className="text-xs text-muted-foreground">
                  {status.last_synced_at ? `Last synced ${relative(status.last_synced_at)}` : "Not synced yet — first sync runs shortly"}
                  {status.last_sync_error && <span className="ml-2 text-red-500">Sync error: {status.last_sync_error}</span>}
                </div>
              </div>
              <ConfirmDelete testid="disconnect-google" title="Disconnect your Google account?"
                description="Synced mail already in Iris stays, but no new mail will be ingested until you reconnect."
                trigger={<Button variant="outline" size="sm" data-testid="disconnect-google-btn"><Unplug className="mr-1.5 h-3.5 w-3.5" />Disconnect</Button>}
                onConfirm={() => disconnect.mutate()} />
            </div>
          </Section>

          {messages.length > 0 && (
            <div className="mb-4"><SearchInput testid="mailbox-search" placeholder="Search sender or subject" value={search} onChange={setSearch} className="w-72" /></div>
          )}

          {messagesLoading ? <Skeleton className="h-64" /> : !messages.length ? (
            <EmptyState icon={Mail} title="No mail synced yet" description="Newly received mail will appear here within a few minutes." />
          ) : !filtered.length ? (
            <EmptyState icon={Mail} title="No messages match" description="Adjust your search." />
          ) : (
            <Tabs defaultValue="needs-attention">
              <TabsList data-testid="mailbox-tabs">
                <TabsTrigger value="needs-attention" data-testid="tab-needs-attention">Needs Attention ({needsAttention.length})</TabsTrigger>
                <TabsTrigger value="addressed" data-testid="tab-addressed">Addressed ({addressedList.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="needs-attention" className="mt-4">
                <Section>
                  {!needsAttention.length ? (
                    <p className="px-4 py-6 text-center text-sm text-muted-foreground">Nothing needs attention right now.</p>
                  ) : (
                    <div className="divide-y divide-border">
                      {needsAttention.map((m) => (
                        <MailRow key={m.id} m={m} atts={attachmentsByMessage[m.gmail_message_id] || []}
                          isOpen={expanded === m.id} pending={toggleAddressed.isPending}
                          onToggleExpand={() => { setExpanded(expanded === m.id ? null : m.id); if (!m.read) markRead.mutate(m.id); }}
                          onToggleAddressed={() => toggleAddressed.mutate({ mid: m.id, addressed: true })}
                          onDelete={() => deleteMessage.mutate(m.id)} />
                      ))}
                    </div>
                  )}
                </Section>
              </TabsContent>

              <TabsContent value="addressed" className="mt-4">
                <Section>
                  {!addressedList.length ? (
                    <p className="px-4 py-6 text-center text-sm text-muted-foreground">Nothing addressed yet.</p>
                  ) : (
                    <div className="divide-y divide-border">
                      {addressedList.map((m) => (
                        <MailRow key={m.id} m={m} atts={attachmentsByMessage[m.gmail_message_id] || []}
                          isOpen={expanded === m.id} pending={toggleAddressed.isPending}
                          onToggleExpand={() => { setExpanded(expanded === m.id ? null : m.id); if (!m.read) markRead.mutate(m.id); }}
                          onToggleAddressed={() => toggleAddressed.mutate({ mid: m.id, addressed: false })}
                          onDelete={() => deleteMessage.mutate(m.id)} />
                      ))}
                    </div>
                  )}
                </Section>
              </TabsContent>
            </Tabs>
          )}
        </>
      )}
    </div>
  );
}
